import { DOCUMENT } from '@angular/common';
import {
  ApplicationConfig,
  provideZonelessChangeDetection,
  provideAppInitializer,
  provideEnvironmentInitializer,
  inject,
  ErrorHandler,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors, withFetch } from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import { forkJoin, of, catchError, switchMap } from 'rxjs';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { routes } from './app.routes';
import { authInterceptor } from '@core/auth/auth.interceptor';
import { httpErrorInterceptor } from '@core/http/http-error.interceptor';
import { provideTranslation } from '@core/i18n/i18n.config';
import { AppLanguageService } from '@core/i18n/app-language.service';
import { provideStorage } from '@core/storage/storage.providers';
import { AuthService } from '@core/auth/auth.service';
import { CompanySessionService } from '@core/auth/company-session.service';
import { provideAnalytics } from '@core/analytics/analytics.providers';
import { CONVERSIONS_API_URL } from '@core/api/conversions-api.service';
import { PRICING_API_URL } from '@core/api/pricing-api.service';
import { SHORTENER_API_URL } from '@core/api/shortener-api.service';
import { environment } from '@env';
import { provideConsent } from '@core/analytics/consent.providers';
import { provideHotjar } from '@core/hotjar/hotjar.providers';
import { provideOpenAiPixel } from '@core/openai-pixel/openai-pixel.providers';
import { BugsnagErrorHandler } from '@bugsnag/plugin-angular';
import { bugsnagBootstrapper } from '@bugsnag/angular-performance';
import { isUnderBase } from '@core/navigation/portal-path';
import { PARTNER_BASE_PATH } from '@core/partner-auth/partner-links';
import { BACKOFFICE_BASE_PATH } from '@core/admin-auth/backoffice-links';

/** Re-hydrates the employer, memberships and active company from the API on startup. */
function refreshSession(auth: AuthService, companySession: CompanySessionService) {
  if (!auth.isAuthenticated()) return of(null);
  return forkJoin([
    auth.fetchEmployer().pipe(catchError((err) => (console.error('Failed to refresh employer', err), of(null)))),
    companySession
      .fetchMemberships()
      .pipe(catchError((err) => (console.error('Failed to refresh memberships', err), of([])))),
  ]).pipe(
    switchMap(() => {
      if (companySession.hasCompanySession()) {
        return companySession.refreshActiveCompanyDetails();
      }

      const storedCompanyId = companySession.activeMembership()?.company.id;
      const memberships = companySession.memberships();
      if (storedCompanyId && memberships.some((m) => m.company.id === storedCompanyId)) {
        return companySession.selectCompany(storedCompanyId);
      }
      return of(null);
    }),
    catchError((err) => (console.error('Failed to refresh company session', err), of(null))),
  );
}

/** Whether the page is inside a portal (Partner Portal, Backoffice), which keeps its own session. */
export function skipsEmployerRefresh(pathname: string, portalBases: readonly string[]): boolean {
  return portalBases.some((base) => isUnderBase(pathname, base));
}

/** Where each portal is mounted. Call in an injection context. */
export function portalBasePaths(): string[] {
  return [inject(PARTNER_BASE_PATH), inject(BACKOFFICE_BASE_PATH)];
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideAppInitializer(() => inject(AppLanguageService).initialize()),
    provideAppInitializer(() => {
      const language = inject(AppLanguageService);
      const auth = inject(AuthService);
      const companySession = inject(CompanySessionService);
      // Portals have their own sessions; a stale employer token must not 401 and
      // redirect a portal user to the employer login. Each portal shell refreshes its own session.
      const onPortal = skipsEmployerRefresh(inject(DOCUMENT).location.pathname, portalBasePaths());
      // Angular runs initializers in parallel, so chain off the language load explicitly:
      // a 401 here logs the user out with a translated toast, and the auth interceptor
      // resolves that message synchronously via TranslateService.instant().
      return language.initialize().pipe(switchMap(() => (onPortal ? of(null) : refreshSession(auth, companySession))));
    }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor, httpErrorInterceptor]), withFetch()),
    ...provideStorage(),
    { provide: CONVERSIONS_API_URL, useValue: environment.conversionsApiUrl },
    { provide: PRICING_API_URL, useValue: environment.pricingApiUrl },
    { provide: SHORTENER_API_URL, useValue: environment.shortenerApiUrl },
    provideToastr({
      positionClass: 'toast-top-right',
      maxOpened: 8,
      autoDismiss: true,
      timeOut: 5000,
      preventDuplicates: true,
    }),
    ...provideTranslation(),
    provideAnalytics(),
    provideConsent(),
    provideHotjar(),
    provideOpenAiPixel(),
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    provideEnvironmentInitializer(() => {
      const registry = inject(MatIconRegistry);
      registry.registerFontClassAlias('uicon-rr', 'fi fi-rr');
      registry.registerFontClassAlias('uicon-sr', 'fi fi-sr');
    }),
    {
      provide: ErrorHandler,
      useFactory: () => new BugsnagErrorHandler(),
    },
    bugsnagBootstrapper,
  ],
};
